/**
 * OSMDExerciseScene - the Exercise Section's playable scene (MUS-317, "7b").
 *
 * A leaf on the shared OSMDPlayableScene base (transport / cursor / accuracy via
 * OSMDEventStream). It binds the session state machine (MUS-315) to that
 * machinery: it provides the ten side-effect actions the machine commands, drives
 * the session lifecycle, and renders the exercise notation the expander produces.
 *
 * Reuse (per the MUS-317 spike): the OSMD render derives its own cursor/note-graph
 * from the rendered score (OSMDIteratorExtensible reads note positions off the
 * render, not a server-side cursor), so the exercise feeds a light, client-built
 * `Phrase` - real MusicXML from the expander, empty cursor arrays - straight
 * through the base's `addAllPhrases`. No backend artifacts, no adapter.
 *
 * Scope of this issue (deliberate, per Phase-1 decomposition):
 * - `gradeResponse` is a PLACEHOLDER verdict; the real timing rule that aggregates
 *   the OSMDEventStream bands (green/yellow/red) is MUS-320.
 * - The React observation seam is a thin callback (`onSessionEvent`); the full
 *   Redux slice + saga is 7c (MUS-318).
 * - Everything is reached only behind the USE_EXERCISES flag via a dev launcher.
 */

import { createActor, type Actor } from 'xstate';
import MusicXML from 'Models/MusicXML';
import Phrase from 'Models/Phrase';
import TimeSignature from 'Models/TimeSignature';
import Logger from 'Utils/Logger';
import { v4 as uuidv4 } from 'uuid';
import OSMDPlayableScene from './OSMDPlayableScene';
import {
  exerciseSessionMachine,
  type SideEffectActionName,
} from 'Phaser/ExerciseSessionMachine/exerciseSessionMachine';
import type {
  ExerciseSessionContext,
  ExerciseSessionEmitted,
  SessionExercise,
} from 'Phaser/ExerciseSessionMachine/exerciseSessionMachine.types';
import type { ScaleCell, ScaleConfig } from 'Types/ExerciseContentTypes';
import type { StepwiseRunPrompt } from 'Types/ExerciseRecipeTypes';
import type { OSMDEventStream } from 'Phaser/GameObjects/OSMDEventStream';
import type { OSMDPhraseDataEventPayload } from 'Phaser/EventBus2/ToPhaser';
import type { PrerenderedPhraseData } from 'Types';
import { expandStepwiseRun, type JudgedNote } from './OSMDExerciseScene.expander';
import { gradeNoteStates, type GradeComputation } from './OSMDExerciseScene.grader';
import { takeKey } from 'Phaser/ExerciseSessionMachine/sessionTraversal';

/** DIVISIONS=4 per quarter in the expander's MusicXML space, so 16 per whole note. */
const WHOLE_NOTE_DIVISIONS = 16;

/**
 * Build the Phrase model's cursor data from the run (F1).
 *
 * `Models/Phrase` computes its own pickup/end timing from `cursorData` in the
 * constructor (`calcPickUpLength` / `calcEndTimestamp` dereference the last datum
 * unconditionally), so an empty array throws and zero-timing is wrong. The OSMD
 * *cursor* is still re-derived from the render at play time; this only supplies the
 * model's timing/layout metadata, in whole-note units, from the same judged run.
 */
const judgedNotesToCursorData = (
  judgedNotes: JudgedNote[],
  [beats, beatType]: [number, number],
): PrerenderedPhraseData[] => {
  const measureDivisions = (beats / beatType) * WHOLE_NOTE_DIVISIONS;
  const byOnset = new Map<number, JudgedNote[]>();
  for (const note of judgedNotes) {
    const at = byOnset.get(note.startDivisions) ?? [];
    at.push(note);
    byOnset.set(note.startDivisions, at);
  }
  return [...byOnset.keys()]
    .sort((a, b) => a - b)
    .map((startDivisions) => ({
      currentTimestamp: startDivisions / WHOLE_NOTE_DIVISIONS,
      currentMeasureIndex: Math.floor(startDivisions / measureDivisions),
      currentVoiceEntries: byOnset.get(startDivisions)!.map((note) => ({
        Notes: note.midiNotes.map((midi) => ({
          fingering: undefined,
          graphics: [],
          isRest: false,
          pitch: {
            accidental: 0,
            fundamentalNote: 0,
            octave: 0,
            halfTone: midi - 12,
            frequency: 440 * Math.pow(2, (midi - 69) / 12),
          },
          noteTie: undefined,
          length: note.durationDivisions / WHOLE_NOTE_DIVISIONS,
          printObject: true,
          staffPosition: note.hand === 'RH' ? 'top' : 'bottom',
        })),
      })),
    }));
};

/** Runtime input for one exercise sitting, handed to the scene at launch. */
/**
 * User-driven lifecycle controls. These are the events a real per-session screen
 * (Phase 3) will drive; the dev launcher stands in for now. `start`/`next` open a
 * take's response window (begin the count-in); `submitTake` ends the take and
 * grades the real played note stream - MUS-319 wired the genuine MIDI-coupled path,
 * replacing the earlier synthesized response.
 */
export interface ExerciseSessionControls {
  start(): void;
  submitTake(): void;
  next(): void;
  end(): void;
}

export interface OSMDExerciseSessionInput {
  prompt: StepwiseRunPrompt;
  exercise: SessionExercise;
  cell: ScaleCell;
  config: ScaleConfig;
  /** Thin observation seam: the machine's emitted events (7c replaces this with
   *  the Redux slice + saga, MUS-318). */
  onSessionEvent?: (event: ExerciseSessionEmitted) => void;
  /** Inbound seam: receives the lifecycle controls once the actor is running. */
  onReady?: (controls: ExerciseSessionControls) => void;
}

type SessionActor = Actor<typeof exerciseSessionMachine>;

/** The base `init` data plus the exercise session input. */
type OSMDExerciseInitData = {
  eventStream: OSMDEventStream;
  width: number;
  height: number;
  mobile?: boolean;
  performanceTempo?: number;
  cursorVisible?: boolean;
  zoomLevel?: number;
  exerciseSession?: OSMDExerciseSessionInput;
} & Record<string, unknown>;

export default class OSMDExerciseScene extends OSMDPlayableScene {
  private exerciseSession: OSMDExerciseSessionInput | null = null;
  private sessionActor: SessionActor | null = null;
  /** The expander's judged-note stream for the loaded run (MUS-320 grades it). */
  private judgedNotes: JudgedNote[] = [];
  /** The last take's computed grade, held for the visible signal (MUS-320). */
  private lastGrade: GradeComputation | null = null;
  /** The reused in-scene gradient + status text. NOT the Phase-3 HUD (MUS-482). */
  private gradeSignalText: Phaser.GameObjects.Text | null = null;

  constructor() {
    super('OSMDExerciseScene');
  }

  init(data: OSMDExerciseInitData): void {
    super.init(data);
    this.exerciseSession = data.exerciseSession ?? null;
  }

  create(): void {
    super.create();
    if (this.exerciseSession) {
      this.startSession(this.exerciseSession);
    }
  }

  /** Create the session actor, wire the emitted-event seam, and seed the sitting. */
  private startSession(input: OSMDExerciseSessionInput): void {
    const actor = createActor(exerciseSessionMachine.provide({ actions: this.sessionActions() }), {
      input: undefined,
    });
    this.sessionActor = actor;

    const forward = (event: ExerciseSessionEmitted) => input.onSessionEvent?.(event);
    actor.on('playthroughCompleted', forward);
    actor.on('pointEarned', forward);
    actor.on('sessionCompleted', forward);

    actor.start();
    actor.send({
      type: 'LOAD_SESSION',
      exercise: input.exercise,
      cell: input.cell,
      config: input.config,
    });

    input.onReady?.({
      // MUS-319: START leaves the press-start screen; RESPONSE_STARTED opens this
      // take's response window (begins the count-in via beginPlayback). The user
      // then plays real MIDI, and submitTake ends the take.
      start: () => {
        actor.send({ type: 'START' });
        actor.send({ type: 'RESPONSE_STARTED' });
      },
      // MUS-319: submit is purely the end-of-take trigger now. It no longer
      // synthesizes a performed response - the performance is the real note stream
      // the grader reads scene-side; the envelope payload stays opaque and empty.
      submitTake: () =>
        actor.send({ type: 'SUBMIT_RESPONSE', response: { kind: 'performed', payload: {} } }),
      // NEXT advances the configuration, then RESPONSE_STARTED reopens the window
      // (a fresh count-in via initPlaythrough -> beginPlayback) for the next take.
      next: () => {
        actor.send({ type: 'NEXT' });
        actor.send({ type: 'RESPONSE_STARTED' });
      },
      end: () => actor.send({ type: 'END_SESSION' }),
    });
  }

  /** The ten side-effect commands the machine issues, bound to the base. */
  private sessionActions(): Record<
    SideEffectActionName,
    (args: { context: ExerciseSessionContext }) => void
  > {
    return {
      loadContent: () => {
        void this.loadRun();
      },
      initPlaythrough: () => {
        // MUS-319: re-arm the inherited transport for a fresh take. softReset stops
        // the base playback machine, resets every NoteContainer + the event stream,
        // and clears playbackStarted so the next beginPlayback enters a fresh
        // count-in. It supersedes the old bare eventStream.reset() (which softReset
        // itself performs), and is what lets repeated takes each start clean.
        this.softReset();
      },
      beginPlayback: () => {
        // MUS-319: open the response window by driving the inherited count-in ->
        // playing transport. This warms the native audio, clicks the count-in
        // metronome, and (critically) sets playbackStarted so the base's MIDI
        // note-on handler forwards played notes into the event stream that colors
        // the graded bands. The old bare phrase.activate() never armed that path, so
        // MIDI was dropped and the bands never moved.
        //
        // Read the tempo from the machine's live currentConfig rather than the
        // static launch input: currentConfig is what the traversal policy ramps
        // (advanceConfig -> planNextConfig), so a future non-manual policy that
        // steps the tempo per take is honored here. currentConfig is always seeded
        // (idle -> LOAD_SESSION -> loading) before this state can be reached, and
        // under the current manual policy it equals the launch tempo, so this is
        // behavior-neutral today.
        this.startExercise({
          tempo: this.sessionActor?.getSnapshot().context.currentConfig?.tempo,
          autoStart: false,
        });
      },
      stopPlayback: () => {
        // MUS-319: halt the transport (metronome + scheduler + TimeKeeper) so the
        // grader reads the take's final band states. Unresolved notes fold to red in
        // the grade, so stopping here freezes an honest picture of what was played.
        this.pauseExercise();
      },
      gradeResponse: () => {
        this.computeGrade();
      },
      // MUS-555: render from the context XState hands the action, NOT the actor's
      // getSnapshot(). These run as state-entry actions inside the grade transition,
      // after processGradeResult has updated `earned`; the actor's snapshot is only
      // committed once the whole transition completes, so reading it here returned the
      // previous take's ledger and the status line lagged one take behind.
      showPassFeedback: ({ context }) => {
        this.renderGradeSignal(context);
      },
      showFailFeedback: ({ context }) => {
        this.renderGradeSignal(context);
      },
      pauseScene: () => {
        this.phrases.forEach((phrase) => phrase.pause());
      },
      resumeScene: () => {
        this.phrases.forEach((phrase) => phrase.activate(true));
      },
      cleanupSession: () => {
        this.phrases.forEach((phrase) => phrase.deactivate());
        this.judgedNotes = [];
        this.lastGrade = null;
        this.gradeSignalText?.destroy();
        this.gradeSignalText = null;
      },
    };
  }

  /** Expand the run, render it through the shared base, then answer SESSION_LOADED. */
  private async loadRun(): Promise<void> {
    const session = this.exerciseSession;
    if (!session) return;
    try {
      const run = expandStepwiseRun(session.prompt, session.cell, session.config);
      this.judgedNotes = run.judgedNotes;
      await this.addAllPhrases([this.toPhrasePayload(run.musicXml, session.prompt)]);
      // Only advance the machine once content has actually rendered. A failed load
      // must not be indistinguishable from success (F3), so SESSION_LOADED is sent
      // here, not in a finally.
      this.sessionActor?.send({ type: 'SESSION_LOADED' });
    } catch (error) {
      Logger.error('[OSMDExerciseScene] loadRun failed; machine stays in loading:', error);
    }
  }

  /** Build the OSMD payload from the expander's MusicXML + a client-built Phrase. */
  private toPhrasePayload(musicXml: string, prompt: StepwiseRunPrompt): OSMDPhraseDataEventPayload {
    const [numerator, denominator] = prompt.time_signature;
    const uuid = uuidv4();
    const cursorData = judgedNotesToCursorData(this.judgedNotes, prompt.time_signature);
    const phraseRaw = new Phrase(
      uuid,
      new MusicXML(musicXml, [new TimeSignature(numerator, denominator)]),
      this.judgedNotes.length,
      '',
      '',
      cursorData,
      cursorData,
      [],
    );
    return {
      phraseRaw,
      phrase: {
        musicXml,
        uuid,
        startTimestamp: 0,
        timeSignature: { numerator, denominator },
        showClef: true,
        showBrace: true,
        showTimeSig: true,
      },
    };
  }

  /**
   * The real per-take grader (MUS-320).
   *
   * Reads each rendered note's final band state from the shared base's stave-note
   * containers and grades the band COMPOSITION (any-red -> failed; zero-red-with-yellow
   * -> clean; all-green -> flawless). It does NOT modify the shared OSMDEventStream /
   * NoteContainer classes and adds NO read-accessor to OSMDEventStream: the per-note
   * bands are read scene-side via the existing container states. `accuracy` is carried
   * as a derived readout only; the gate is the composition, not the number.
   *
   * With real MIDI driving the stream (MUS-319), a played take grades its actual
   * bands. A take that rendered notes but captured no input at all is flagged
   * `noInput`: the scene routes it to the non-scoring path so an empty submit is a
   * genuine no-op (it neither clears nor breaks a streak), and the signal reads "no
   * input recorded" instead of a misleading all-red gradient.
   */
  private computeGrade(): void {
    const states = this.phrases
      .flatMap((phrase) => phrase.getStaveNoteContainers())
      .map((container) => container.getState());
    const grade = gradeNoteStates(states);
    this.lastGrade = grade;
    // MUS-319: a take with rendered notes but no played input is a no-op, not a
    // fail. Route it to the non-scoring GRADE_NO_INPUT event so an empty submit can
    // never break a mastery streak; the signal still reads "no input recorded".
    if (grade.noInput) {
      queueMicrotask(() => this.sessionActor?.send({ type: 'GRADE_NO_INPUT' }));
      return;
    }
    const { accuracy, verdict } = grade;
    // Deferred to avoid a re-entrant send during the evaluating transition.
    queueMicrotask(() => this.sessionActor?.send({ type: 'GRADE_RESULT', accuracy, verdict }));
  }

  /**
   * The visible signal: a minimal, honest gradient + the resulting
   * cell-status language - never a PASS / FAIL stamp. Rendered as plain in-scene text
   * so it does not depend on the real app shell (MUS-481); the bespoke grading HUD is
   * MUS-482 (Phase 3) and is deliberately NOT built here.
   */
  private renderGradeSignal(context: ExerciseSessionContext): void {
    const grade = this.lastGrade;
    if (!grade) return;

    // This runs as a machine-commanded side effect (showPass/FailFeedback). The
    // visible signal is informational, so a render failure must never abort the
    // session machine - swallow and log rather than propagate into the transition.
    try {
      const message = grade.noInput
        ? 'No input recorded - take not graded'
        : `${grade.accuracy}% accurate  -  green ${grade.breakdown.green} / ` +
          `yellow ${grade.breakdown.yellow} / red ${grade.breakdown.red}\n${cellStatusLine(context)}`;
      this.drawGradeSignal(message);
    } catch (error) {
      Logger.error('[OSMDExerciseScene] grade-signal render failed:', error);
    }
  }

  /** Draw or update the single reused signal text object (top-left, fixed to camera). */
  private drawGradeSignal(message: string): void {
    if (this.gradeSignalText) {
      this.gradeSignalText.setText(message);
      return;
    }
    this.gradeSignalText = this.add
      .text(16, 16, message, {
        fontFamily: 'sans-serif',
        fontSize: '16px',
        color: '#e8e8e8',
        backgroundColor: '#00000080',
        padding: { x: 8, y: 6 },
      })
      .setScrollFactor(0)
      .setDepth(1000);
  }
}

/**
 * The user-facing cell status (Untouched -> Attempted -> Cleared -> Mastered)
 * for the current cell, read from the given session context. Callers pass the
 * post-grade context the machine hands its feedback actions (see showPass/FailFeedback)
 * so the line reflects THIS take. We CONSUME the machine's clearance / mastery policy
 * result rather than re-deriving the ladder. Never a pass/fail.
 */
export function cellStatusLine(context: ExerciseSessionContext): string {
  if (context.currentCell && context.currentConfig) {
    const earned = context.earned[takeKey(context.currentCell, context.currentConfig)];
    if (earned?.mastered) return 'Mastered';
    if (earned?.cleared) return 'Cleared';
  }
  return 'Not yet cleared';
}
